import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export interface UsePaginationOptions {
  initialPage?: number;
  initialLimit?: number;
  totalPages?: number;
  syncUrl?: boolean;
}

export function usePagination({
  initialPage = 1,
  initialLimit = 10,
  syncUrl = true,
}: UsePaginationOptions = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Inicializa a partir da URL, se existir e syncUrl for true
  const pageFromUrl = syncUrl
    ? Number(searchParams.get("page")) || initialPage
    : initialPage;
  const limitFromUrl = syncUrl
    ? Number(searchParams.get("limit")) || initialLimit
    : initialLimit;

  const [currentPage, setCurrentPageState] = useState(pageFromUrl);
  const [limit, setLimitState] = useState(limitFromUrl);
  const [totalPages, setTotalPages] = useState(1);

  // Atualiza a URL ao mudar página ou limite
  useEffect(() => {
    if (!syncUrl) return;
    const params = new URLSearchParams(searchParams);
    params.set("page", String(currentPage));
    params.set("limit", String(limit));
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line
  }, [currentPage, limit, syncUrl]);

  // Atualiza o estado se a URL mudar externamente
  useEffect(() => {
    if (!syncUrl) return;
    const urlPage = Number(searchParams.get("page")) || initialPage;
    const urlLimit = Number(searchParams.get("limit")) || initialLimit;
    if (urlPage !== currentPage) setCurrentPageState(urlPage);
    if (urlLimit !== limit) setLimitState(urlLimit);
    // eslint-disable-next-line
  }, [searchParams, syncUrl]);

  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page);
  }, []);

  const setLimit = useCallback((newLimit: number) => {
    setLimitState(newLimit);
    setCurrentPageState(1); // Resetar página ao mudar limite
  }, []);

  const resetPagination = useCallback(() => {
    setCurrentPageState(1);
  }, []);

  return {
    currentPage,
    setCurrentPage,
    limit,
    setLimit,
    totalPages,
    setTotalPages,
    resetPagination,
  };
}
